import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Pod24AdminLayout } from "../../components/Pod24Admin/Pod24AdminLayout";
import { adminWithdrawalApiService } from "infrastructure/api/admin/admin-withdrawal-api-service";
import type {
  WithdrawalRequest,
  WithdrawalRequestStatus,
} from "domain/types/withdrawal";

const FONT_FAMILY = "Pretendard, -apple-system, BlinkMacSystemFont, sans-serif";
const PAGE_SIZE = 50;

const thClass =
  "bg-[#F8F8F8] text-[#555] font-medium text-[12.5px] py-3 px-[18px] text-left border-b border-[#D9D9D9]";
const tdClass = "py-3 px-[18px] text-[13px] border-b border-[#D9D9D9]";
const inputClass =
  "w-full border border-[#D9D9D9] rounded-lg px-3 py-2 text-[13px] outline-none focus:border-[#4285F4] bg-white";

type TabKey = "SCHEDULED" | "COMPLETED" | "CANCELLED" | "ALL";

const TABS: { key: TabKey; label: string; status: WithdrawalRequestStatus | null }[] = [
  { key: "SCHEDULED", label: "종료 예약중", status: "SCHEDULED" },
  { key: "COMPLETED", label: "종료 완료", status: "COMPLETED" },
  { key: "CANCELLED", label: "취소됨", status: "CANCELLED" },
  { key: "ALL", label: "전체", status: null },
];

/**
 * 어드민 "운영 종료 예약 현황".
 *
 * 승인 단계는 없다. 회사가 신청하면 바로 예약되고 7일 뒤 실행된다.
 * 여기서는 진행 상황을 보고, 사고가 났을 때 예약을 취소한다.
 */
const WithdrawalRequests: React.FC = () => {
  const [tab, setTab] = useState<TabKey>("SCHEDULED");
  const [keyword, setKeyword] = useState("");
  const [requests, setRequests] = useState<WithdrawalRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [cancelTarget, setCancelTarget] = useState<WithdrawalRequest | null>(null);

  const status = useMemo(() => TABS.find((t) => t.key === tab)?.status ?? null, [tab]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const page = await adminWithdrawalApiService.list({
        status,
        keyword: keyword.trim() || null,
        page: 0,
        size: PAGE_SIZE,
      });
      setRequests(page.items ?? []);
    } catch (e) {
      setError(e instanceof Error ? e.message : "목록을 불러오지 못했습니다.");
      setRequests([]);
    } finally {
      setLoading(false);
    }
  }, [status, keyword]);

  useEffect(() => {
    load();
  }, [load]);

  const runAction = async (action: () => Promise<unknown>) => {
    setError("");

    try {
      await action();
      setCancelTarget(null);
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "처리에 실패했습니다.");
      setCancelTarget(null);
    }
  };

  return (
    <Pod24AdminLayout>
      <div className="text-[#252525]" style={{ fontFamily: FONT_FAMILY }}>
        <div className="mb-6">
          <h1 className="text-[22px] font-bold mb-1.5">운영 종료 예약 현황</h1>
          <p className="text-[13px] text-[#888]">
            회사가 종료를 신청하면 <b className="font-bold">7일 뒤</b>에 자동으로 실행됩니다.
            별도 승인 절차는 없으며, 필요할 때 예약을 취소할 수 있습니다.
          </p>
        </div>

        <div className="flex gap-2 items-center flex-wrap mb-4">
          <div className="inline-flex bg-[#F2F2F2] rounded-lg p-[3px] gap-[2px]">
            {TABS.map((item) => (
              <button
                key={item.key}
                type="button"
                onClick={() => setTab(item.key)}
                className={`px-3.5 py-[7px] rounded-md text-[13px] ${
                  tab === item.key
                    ? "bg-white text-[#252525] font-medium shadow-sm"
                    : "text-[#555]"
                }`}
              >
                {item.label}
              </button>
            ))}
          </div>
          <div className="w-[280px]">
            <input
              className={inputClass}
              placeholder="회사명 · 신청자 · 이메일 검색"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
            />
          </div>
        </div>

        {error && (
          <div className="rounded-lg px-4 py-3 mb-4 text-[13px] bg-[#EBF3FF] text-[#4285F4] border border-[#4285F4]/20">
            {error}
          </div>
        )}

        <div className="rounded-lg overflow-x-auto border border-[#D9D9D9] bg-white">
          <table className="w-full" style={{ borderCollapse: "separate", borderSpacing: 0 }}>
            <thead>
              <tr>
                <th className={thClass}>회사명</th>
                <th className={`${thClass} w-[200px]`}>신청자</th>
                <th className={`${thClass} w-[100px]`}>소속 회원</th>
                <th className={thClass}>사유</th>
                <th className={`${thClass} w-[110px]`}>신청일</th>
                <th className={`${thClass} w-[150px]`}>상태</th>
                <th className={`${thClass} w-[130px] text-right`}>처리</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={7} className="py-8 px-[18px] text-[13px] text-center text-[#888]">
                    불러오는 중...
                  </td>
                </tr>
              )}

              {!loading && requests.length === 0 && (
                <tr>
                  <td colSpan={7} className="py-8 px-[18px] text-[13px] text-center text-[#888]">
                    신청이 없습니다.
                  </td>
                </tr>
              )}

              {!loading &&
                requests.map((request) => (
                  <tr key={request.id} className="hover:bg-[#F2F2F2]">
                    <td className={`${tdClass} font-medium`}>{request.companyName || "—"}</td>
                    <td className={tdClass}>
                      <div>{request.userName || "—"}</div>
                      <div className="text-[12px] text-[#888]">{request.userEmail || "—"}</div>
                    </td>
                    <td className={tdClass}>
                      {request.memberCount === null ? "—" : `${request.memberCount}명`}
                    </td>
                    <td className={tdClass}>{request.reason || "—"}</td>
                    <td className={tdClass}>{formatDate(request.requestedAt)}</td>
                    <td className={tdClass}>
                      <StatusBadge request={request} />
                    </td>
                    <td className={`${tdClass} text-right whitespace-nowrap`}>
                      <RowActions request={request} onCancel={setCancelTarget} />
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>

      {cancelTarget && (
        <CancelDialog
          request={cancelTarget}
          onClose={() => setCancelTarget(null)}
          onConfirm={() => runAction(() => adminWithdrawalApiService.cancel(cancelTarget.id))}
        />
      )}
    </Pod24AdminLayout>
  );
};

const RowActions: React.FC<{
  request: WithdrawalRequest;
  onCancel: (request: WithdrawalRequest) => void;
}> = ({ request, onCancel }) => {
  if (request.status === "SCHEDULED") {
    return (
      <button
        type="button"
        onClick={() => onCancel(request)}
        className="px-3 py-1.5 rounded-md text-[13px] bg-white text-[#4285F4] border border-[#4285F4] hover:bg-[#E8EFFF]"
      >
        예약 취소
      </button>
    );
  }

  return (
    <span className="text-[#888]">
      {request.processedAt ? `${formatDate(request.processedAt)} 처리` : "—"}
    </span>
  );
};

const StatusBadge: React.FC<{ request: WithdrawalRequest }> = ({ request }) => {
  if (request.status === "SCHEDULED") {
    const remaining = daysUntil(request.scheduledDeletedAt);

    return (
      <>
        <span className="inline-block px-2 py-0.5 rounded-[4px] text-[11px] font-medium bg-[#E8EFFF] text-[#4285F4]">
          {remaining === null ? "종료 예약" : `종료 예약 D-${remaining}`}
        </span>
        <div className="text-[12px] text-[#888] mt-0.5">
          {formatDate(request.scheduledDeletedAt)} 실행
        </div>
      </>
    );
  }

  const label: Record<WithdrawalRequestStatus, string> = {
    SCHEDULED: "종료 예약",
    COMPLETED: "종료 완료",
    CANCELLED: "취소됨",
  };

  return (
    <span
      className="inline-block px-2 py-0.5 rounded-[4px] text-[11px] font-medium bg-[#f5f5f5] text-[#888]"
    >
      {label[request.status]}
    </span>
  );
};

const CancelDialog: React.FC<{
  request: WithdrawalRequest;
  onClose: () => void;
  onConfirm: () => void;
}> = ({ request, onClose, onConfirm }) => (
  <Dialog onClose={onClose}>
    <h3 className="text-[16px] font-bold text-[#222] mb-2.5">종료 예약을 취소하시겠습니까?</h3>
    <p className="text-[13px] text-[#555] mb-5">
      <b className="font-bold">{request.companyName || "—"}</b> 의 예약된 종료가 취소되고 쇼핑몰이
      정상 운영으로 돌아갑니다. 신규 주문 차단도 함께 해제되며, 다시 종료하려면 처음부터 신청해야
      합니다.
    </p>
    <DialogActions onClose={onClose} confirmLabel="예약 취소" onConfirm={onConfirm} />
  </Dialog>
);

const Dialog: React.FC<{ onClose: () => void; children: React.ReactNode }> = ({
  onClose,
  children,
}) => (
  <div className="fixed inset-0 z-[6000] flex items-center justify-center p-5">
    <div className="absolute inset-0 bg-black opacity-50" onClick={onClose} />
    <div className="relative z-[6001] bg-white rounded-[12px] p-6 w-full max-w-[460px]">
      {children}
    </div>
  </div>
);

const DialogActions: React.FC<{
  onClose: () => void;
  confirmLabel: string;
  confirmDisabled?: boolean;
  confirmTone?: "default" | "danger";
  onConfirm: () => void;
}> = ({ onClose, confirmLabel, confirmDisabled, confirmTone = "default", onConfirm }) => (
  <div className="flex gap-2 justify-end mt-5">
    <button
      type="button"
      onClick={onClose}
      className="px-4 py-2 rounded-md text-[13px] border border-[#222] text-[#222] hover:bg-[#f5f5f5]"
    >
      취소
    </button>
    <button
      type="button"
      disabled={confirmDisabled}
      onClick={onConfirm}
      className={`px-4 py-2 rounded-md text-[13px] text-white ${
        confirmDisabled
          ? "bg-[#e0e0e0] text-[#bbb] cursor-not-allowed"
          : confirmTone === "danger"
            ? "bg-[#222] hover:bg-[#333]"
            : "bg-[#222] hover:bg-[#333]"
      }`}
    >
      {confirmLabel}
    </button>
  </div>
);

const formatDate = (value?: string | null): string => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleDateString("ko-KR");
};

/** 남은 일수. 이미 지났으면 0 */
const daysUntil = (value?: string | null): number | null => {
  if (!value) return null;
  const target = new Date(value).getTime();
  if (Number.isNaN(target)) return null;
  return Math.max(0, Math.ceil((target - Date.now()) / (1000 * 60 * 60 * 24)));
};

export default WithdrawalRequests;
